import React from "react";
import { EyeIcon, EyeSlashIcon } from "@heroicons/react/20/solid";
import { Badge } from "../../../ui/Badge";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "../../../ui/Tooltip";
import { cn } from "../../../ui/utils";
import type { FailureMode, Granularity } from "../types";
import { UNASSIGNED_COLOR } from "../utils/palette";
import { modeTrend } from "../utils/trend";
import { DEFAULT_VISIBLE_COUNT } from "../utils/visibleModes";
import {
  newBadgeExplanation,
  trendAriaLabel,
  trendExplanation,
  windowNoun,
} from "../utils/windowCopy";

interface ModeLegendProps {
  modes: FailureMode[];
  selectedModeId: string | null;
  onSelect: (id: string) => void;
  /** Session-stable palette colour for a mode id (see `useModeColors`). */
  colorForMode: (id: string) => string;
  /** Ids of the modes drawn on the map; the rest are toggled off. */
  visibleModeIds: Set<string>;
  /** Show/hide a single mode on the map. */
  onToggleVisibility: (id: string) => void;
  /** Draw every mode. */
  onShowAll: () => void;
  /** Draw no modes. */
  onHideAll: () => void;
  /** Back to the default — the largest few modes. */
  onResetTop: () => void;
  unassignedCount: number;
  /** Whether the grey unassigned long tail is drawn on the map. */
  showUnassigned: boolean;
  onToggleUnassigned: () => void;
  granularity: Granularity;
  /** True on the live "now" view; false when scrubbed back in history. The
   * "New" badge only renders live — "new" in a historical window reads as
   * "new right now" and confuses (#1273). */
  isLive: boolean;
}

/** A compact text button for the legend's bulk visibility controls. */
function LegendControl({
  onClick,
  children,
}: {
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="rounded px-1.5 py-0.5 text-xs text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
    >
      {children}
    </button>
  );
}

/**
 * ▲/▼ delta vs. the previous window, with the comparison spelled out inline
 * ("vs prev week") and in a tooltip (#1273). Growing failures are bad (red);
 * shrinking, good (green).
 */
function TrendIndicator({
  size,
  prev,
  granularity,
}: {
  size: number;
  prev: number;
  granularity: Granularity;
}) {
  const { delta, direction, pct } = modeTrend(size, prev);
  const noun = windowNoun(granularity);
  const up = direction === "up";
  const pctText = pct === null ? "" : ` (${Math.abs(pct)}%)`;
  // One aria-label carrying the full meaning — direction, magnitude, and the
  // comparison window — so SR users don't lose the delta to a bare
  // "increasing" (#1273). The visible glyph/sign is then decorative.
  const ariaLabel = trendAriaLabel(granularity, direction, delta, pct);
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        {direction === "flat" ? (
          <span className="text-muted-foreground" aria-label={ariaLabel}>
            — no change vs prev {noun}
          </span>
        ) : (
          <span
            className={up ? "text-destructive" : "text-success"}
            aria-label={ariaLabel}
          >
            {up ? "▲" : "▼"} {delta > 0 ? "+" : ""}
            {delta}
            {pctText} vs prev {noun}
          </span>
        )}
      </TooltipTrigger>
      <TooltipContent>{trendExplanation(granularity)}</TooltipContent>
    </Tooltip>
  );
}

/**
 * The mode legend — the heart of the page (design doc 015 §6): every failure
 * mode with its colour, label, in-window size, window-over-window delta, and
 * new/dormant badges. Selecting a row highlights that mode on the map and opens
 * its detail panel.
 */
export function ModeLegend({
  modes,
  selectedModeId,
  onSelect,
  colorForMode,
  visibleModeIds,
  onToggleVisibility,
  onShowAll,
  onHideAll,
  onResetTop,
  unassignedCount,
  showUnassigned,
  onToggleUnassigned,
  granularity,
  isLive,
}: ModeLegendProps) {
  const shownCount = modes.reduce(
    (n, m) => n + (visibleModeIds.has(m.failure_mode_id) ? 1 : 0),
    0,
  );
  return (
    <TooltipProvider>
      <div className="flex h-full flex-col rounded-lg border border-border bg-card">
        <div className="flex items-center justify-between border-b border-border px-4 py-3">
          <div className="flex items-center gap-2">
            <h2 className="text-sm font-semibold text-foreground">
              Failure modes
            </h2>
            <Badge variant="outline">
              {shownCount === modes.length
                ? modes.length
                : `${shownCount}/${modes.length}`}
            </Badge>
          </div>
          {modes.length > 0 && (
            <div className="flex items-center gap-0.5">
              <LegendControl onClick={onResetTop}>
                Top {DEFAULT_VISIBLE_COUNT}
              </LegendControl>
              <LegendControl onClick={onShowAll}>All</LegendControl>
              <LegendControl onClick={onHideAll}>None</LegendControl>
            </div>
          )}
        </div>

        <div className="flex-1 overflow-y-auto p-2">
          {modes.length === 0 ? (
            <p className="px-2 py-4 text-sm text-muted-foreground">
              No failure modes discovered yet.
            </p>
          ) : (
            <ul className="space-y-1">
              {modes.map((mode) => {
                const selected = mode.failure_mode_id === selectedModeId;
                const visible = visibleModeIds.has(mode.failure_mode_id);
                return (
                  <li key={mode.failure_mode_id}>
                    <div
                      className={cn(
                        "flex items-stretch gap-1 rounded-md transition-colors",
                        selected && "bg-muted ring-1 ring-primary",
                      )}
                    >
                      <button
                        type="button"
                        onClick={() => onToggleVisibility(mode.failure_mode_id)}
                        aria-pressed={visible}
                        aria-label={
                          visible
                            ? `Hide ${mode.label} on the map`
                            : `Show ${mode.label} on the map`
                        }
                        className="flex flex-shrink-0 items-center rounded-md px-2 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
                      >
                        {visible ? (
                          <EyeIcon className="h-4 w-4" />
                        ) : (
                          <EyeSlashIcon className="h-4 w-4" />
                        )}
                      </button>
                      <button
                        type="button"
                        onClick={() => onSelect(mode.failure_mode_id)}
                        aria-pressed={selected}
                        className={cn(
                          "min-w-0 flex-1 rounded-md py-2 pr-2 text-left transition-colors hover:bg-muted",
                          !visible && "opacity-50",
                        )}
                      >
                        <div className="flex items-center gap-2">
                          <span
                            className="h-3 w-3 flex-shrink-0 rounded-full"
                            style={{
                              backgroundColor: colorForMode(mode.failure_mode_id),
                            }}
                            aria-hidden="true"
                          />
                          <span className="flex-1 truncate text-sm font-medium text-foreground">
                            {mode.label}
                          </span>
                          <span className="flex-shrink-0 font-mono text-sm text-foreground">
                            {mode.size_in_window}
                          </span>
                        </div>
                        <div className="mt-1 flex flex-wrap items-center gap-2 pl-5 text-xs">
                          <TrendIndicator
                            size={mode.size_in_window}
                            prev={mode.prev_window_size}
                            granularity={granularity}
                          />
                          {isLive && mode.is_new && (
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <span>
                                  <Badge variant="success">New</Badge>
                                </span>
                              </TooltipTrigger>
                              <TooltipContent>
                                {newBadgeExplanation(granularity)}
                              </TooltipContent>
                            </Tooltip>
                          )}
                          {mode.status === "dormant" && <Badge>Dormant</Badge>}
                        </div>
                      </button>
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </div>

        {unassignedCount > 0 && (
          <button
            type="button"
            onClick={onToggleUnassigned}
            aria-pressed={showUnassigned}
            aria-label={
              showUnassigned
                ? "Hide unclustered failures on the map"
                : "Show unclustered failures on the map"
            }
            className={cn(
              "flex items-center gap-2 border-t border-border px-4 py-3 text-xs text-muted-foreground transition-colors hover:bg-muted hover:text-foreground",
              !showUnassigned && "opacity-60",
            )}
          >
            {showUnassigned ? (
              <EyeIcon className="h-3.5 w-3.5 flex-shrink-0" />
            ) : (
              <EyeSlashIcon className="h-3.5 w-3.5 flex-shrink-0" />
            )}
            <span
              className="h-2.5 w-2.5 flex-shrink-0 rounded-full"
              style={{ backgroundColor: UNASSIGNED_COLOR }}
              aria-hidden="true"
            />
            {unassignedCount.toLocaleString()} unclustered
          </button>
        )}
      </div>
    </TooltipProvider>
  );
}
